import { arrowLeft } from 'assets';
import { DatePickerField, Modal, SelectField } from 'components';
import Button from 'components/Button';
import ButtonSubmit from 'components/Forms/ButtonSubmit';
import { TIME_CHANGE_FLAG } from 'constant';
import { FormikProvider, useFormik } from 'formik';
import { Form } from 'formik-antd';
import { useAppDispatch } from 'hooks';
import { dayjs, yup } from 'libs';
import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { PostStampRequest, WorkerStatus } from 'types';
import { postStamp } from '../thunk';
import SectionStyled, { HeaderStyle } from './styled';

interface ModalEditCheckInTimeProps {
  visible: boolean;
  onCancel: (cancel: boolean) => void;
  onOk: () => void;
  workerStatus?: WorkerStatus;
}

const ModalEditCheckInTime: FC<ModalEditCheckInTimeProps> = ({
  visible,
  workerStatus,
  onCancel,
  onOk,
}) => {
  const dispatch = useAppDispatch();
  // 2023/03/20 Leading Win 複数業者コード紐付きNo88の対応 start
  const { buildingId, gyosya_code, gyosya_siten_code } = useParams<{
    buildingId: string;
    gyosya_code: string;
    gyosya_siten_code: string;
  }>();
  // 2023/03/20 Leading Win 複数業者コード紐付きNo88の対応 start
  const generateDate = useMemo(() => {
    let date: string | undefined;
    let start_hour: number | null = null;
    let start_minute: number | null = null;
    let end_hour: number | null = null;
    let end_minute: number | null = null;

    if (workerStatus?.enter_stamp) {
      const enter = dayjs(workerStatus.enter_stamp);
      if (enter.isValid()) {
        date = enter.format('YYYY/MM/DD');
        start_hour = enter.hour();
        start_minute = enter.minute();
      }
    }
    if (workerStatus?.exit_stamp) {
      const exit = dayjs(workerStatus.exit_stamp);
      if (exit.isValid()) {
        if (!date) {
          date = exit.format('YYYY/MM/DD');
        }
        end_hour = exit.hour();
        end_minute = exit.minute();
      }
    }

    if (start_hour === null && end_hour !== null) {
      start_hour = end_hour;
    }
    if (start_minute === null && end_minute !== null) {
      start_minute = end_minute;
    }
    if (end_hour === null && start_hour !== null) {
      end_hour = start_hour;
    }
    if (end_minute === null && start_minute !== null) {
      end_minute = start_minute;
    }

    if (start_hour === null && end_hour === null) {
      start_hour = 0;
      end_hour = 23;
    }

    if (start_minute === null && end_minute === null) {
      start_minute = 0;
      end_minute = 59;
    }
    return { date, start_hour, start_minute, end_hour, end_minute };
  }, [workerStatus?.enter_stamp, workerStatus?.exit_stamp]);
  const initialValues = {
    worker_name: workerStatus?.user_name,
    site_name: workerStatus?.cooperator_name,
    date: generateDate.date,
    start_hour: String(generateDate.start_hour),
    start_minute: String(generateDate?.start_minute),
    end_hour: String(generateDate?.end_hour),
    end_minute: String(generateDate?.end_minute),
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    async onSubmit(values) {
      const day = dayjs(values.date).format('YYYY-MM-DD');
      const timeStart = dayjs(`${day} ${values.start_hour}:${values.start_minute}`).format(
        'YYYY-MM-DD HH:mm'
      );
      const timeEnd = dayjs(`${day} ${values.end_hour}:${values.end_minute}`).format(
        'YYYY-MM-DD HH:mm'
      );

      const enter_time_change_flag = () => {
        let result = TIME_CHANGE_FLAG.NOT_CHANGE;
        if (values.start_hour !== initialValues.start_hour) {
          result = TIME_CHANGE_FLAG.CHANGE;
        }
        if (values.start_minute !== initialValues.start_minute) {
          result = TIME_CHANGE_FLAG.CHANGE;
        }
        return result;
      };
      const leave_time_change_flag = () => {
        let result = TIME_CHANGE_FLAG.NOT_CHANGE;
        if (values.end_hour !== initialValues.end_hour) {
          result = TIME_CHANGE_FLAG.CHANGE;
        }
        if (values.end_minute !== initialValues.end_minute) {
          result = TIME_CHANGE_FLAG.CHANGE;
        }
        return result;
      };

      const dataPostStamp = () => {
        let result: PostStampRequest = {
          user_id: workerStatus?.user_id,
          building_id: buildingId,
          working_day: dayjs(values.date).toDate(),
          enter_time_change_flag: TIME_CHANGE_FLAG.NOT_CHANGE,
          leave_time_change_flag: TIME_CHANGE_FLAG.NOT_CHANGE,
        };
        if (enter_time_change_flag() === TIME_CHANGE_FLAG.CHANGE) {
          result = {
            ...result,
            enter_time_change_flag: enter_time_change_flag(),
            before_enter_record_time:
              workerStatus?.enter_stamp &&
              dayjs(workerStatus?.enter_stamp).format('YYYY/MM/DD HH:mm'),
            after_enter_record_time: timeStart,
            before_leave_record_time:
              workerStatus?.exit_stamp ??
              dayjs(workerStatus?.exit_stamp).format('YYYY/MM/DD HH:mm'),
            // 2023/03/20 Leading Win 複数業者コード紐付きNo88の対応 start
            gyosya_code: gyosya_code,
            gyosya_siten_code: gyosya_siten_code,
            // 2023/03/20 Leading Win 複数業者コード紐付きNo88の対応 end
          };
        }
        if (leave_time_change_flag() === TIME_CHANGE_FLAG.CHANGE) {
          result = {
            ...result,
            leave_time_change_flag: leave_time_change_flag(),
            before_leave_record_time:
              workerStatus?.exit_stamp ??
              dayjs(workerStatus?.exit_stamp).format('YYYY/MM/DD HH:mm'),
            after_leave_record_time: timeEnd,
            before_enter_record_time:
              workerStatus?.enter_stamp &&
              dayjs(workerStatus?.enter_stamp).format('YYYY/MM/DD HH:mm'),
            // 2023/03/20 Leading Win 複数業者コード紐付きNo88の対応 start
            gyosya_code: gyosya_code,
            gyosya_siten_code: gyosya_siten_code,
            // 2023/03/20 Leading Win 複数業者コード紐付きNo88の対応 end
          };
        }
        return result;
      };

      await dispatch(postStamp({ ...dataPostStamp() })).then((res) => onOk());
    },
  });

  const dateFormat = 'YYYY/MM/DD';
  const onCancelButton = () => {
    onCancel(false);
    formik.resetForm();
  };
  const Header: FC = () => {
    return (
      <HeaderStyle onClick={onCancelButton}>
        <img src={arrowLeft} alt="" />
        <div onClick={onCancelButton}>入退場時刻補正</div>
      </HeaderStyle>
    );
  };

  return (
    <>
      <Modal title={<Header />} visible={visible} footer={false} width={560}>
        <SectionStyled>
          <FormikProvider value={formik}>
            <Form layout="vertical" className="form">
              <div className="group-input">
                <div className="box-item">
                  <Form.Item
                    className="input-onsite"
                    label="作業員名"
                    colon={false}
                    name="worker_name"
                  >
                    <div className="worker_name">{initialValues.worker_name}</div>
                  </Form.Item>
                </div>
                <div className="box-item">
                  <Form.Item className="input-onsite" label="業者名" colon={false} name="siteName">
                    <div className="siteName">{initialValues.site_name}</div>
                  </Form.Item>
                </div>
                <div className="box-item">
                  <Form.Item className="input-onsite" label="日付" colon={false} name="date">
                    <DatePickerField
                      format={dateFormat}
                      name="date"
                      className="select-date"
                      placeholder=""
                    />
                  </Form.Item>
                </div>
                <div className="box-select-time">
                  <Form.Item
                    className="input-time"
                    label="入退場時刻"
                    colon={false}
                    name="start_hour"
                  >
                    <SelectField
                      suffixIcon={<i className="triangle-down" />}
                      data={hours}
                      className="select-time"
                      name="start_hour"
                    />
                    <div className="center-text">:</div>
                    <SelectField
                      suffixIcon={<i className="triangle-down" />}
                      data={minutes}
                      className="select-time"
                      name="start_minute"
                    />
                  </Form.Item>
                  <div className="center-text-separate">〜</div>
                  <Form.Item className="input-time" label={<span />} name="end_hour">
                    <div className="custom-mess-error">
                      <div className="custom-mess-error-2">
                        <SelectField
                          suffixIcon={<i className="triangle-down" />}
                          data={hours}
                          className="select-time"
                          name="end_hour"
                        />
                        <div className="center-text">:</div>
                        <SelectField
                          suffixIcon={<i className="triangle-down" />}
                          data={minutes}
                          className="select-time"
                          name="end_minute"
                        />
                      </div>
                      {formik.errors.end_hour ? (
                        <div />
                      ) : (
                        formik.errors.end_minute && (
                          <div className="mess-error">{formik.errors.end_minute}</div>
                        )
                      )}
                    </div>
                  </Form.Item>
                </div>
              </div>
              <div className="ant-modal-footer">
                <Button className="button-secondary" onClick={onCancelButton}>
                  キャンセル
                </Button>
                <ButtonSubmit disabled={!formik.isValid || !formik.dirty}>確定</ButtonSubmit>
              </div>
            </Form>
          </FormikProvider>
        </SectionStyled>
      </Modal>
    </>
  );
};

export default ModalEditCheckInTime;

const hours = Array.from({ length: 24 }, (x, i) =>
  i < 10 ? { label: `0${i}`, value: i } : { label: `${i}`, value: i }
);

const minutes = Array.from({ length: 60 }, (x, i) =>
  i < 10 ? { label: `0${i}`, value: i } : { label: `${i}`, value: i }
);

const validationSchema = yup.object().shape({
  date: yup.string().nullable().required('日付を選択してください'),
  start_hour: yup.number().nullable().required(),
  start_minute: yup.number().nullable().required(),
  end_hour: yup
    .number()
    .min(yup.ref('start_hour'), '退場時刻は入場時刻以降の時刻を入力してください')
    .nullable()
    .required(),
  end_minute: yup
    .number()
    .test('check', '退場時刻は入場時刻以降の時刻を入力してください', (end_minute, context) => {
      const { start_minute, start_hour, end_hour } = context.parent;
      const startDate = dayjs(`${start_hour}:${start_minute}`, 'H:m');
      const endDate = dayjs(`${end_hour}:${end_minute}`, 'H:m');
      return startDate < endDate;
    })
    .nullable()
    .required(),
});
